/**
 * El formulario del cotizador de www.sunsmartec.com.
 *
 * Pide lo minimo: nombre, correo, zona y con que consumo trabajar. Cada campo mas es
 * gente que abandona a mitad, y todo lo demas —la potencia, los paneles, el ahorro— lo
 * calcula el motor a partir de esto. La ubicacion es opcional porque un permiso
 * obligatorio del navegador cuesta mas clientes de los que mejora el calculo.
 */

import { useEffect, useState } from 'react'

import {
  cargarFormulario,
  enviarSolicitud,
  pedirRecomendacion,
  pedirUbicacion,
  type DatosDelFormulario,
  type OpcionesDelFormulario,
  type Recomendacion as DatosRecomendacion,
} from './api.js'
import { BloqueConsumo } from './componentes/BloqueConsumo.js'
import { CampoTexto } from './componentes/CampoTexto.js'
import { Gracias } from './componentes/Gracias.js'
import { Recomendacion } from './componentes/Recomendacion.js'

/**
 * Lo que se espera desde la ultima tecla antes de pedir la recomendacion.
 *
 * Sin esta pausa se dispara una peticion por cada digito: quien escribe "1000" manda
 * cuatro, y las tres primeras son para consumos que no existen (1, 10, 100). Medio
 * segundo es lo que tarda una persona en terminar de teclear un numero.
 */
const PAUSA_ANTES_DE_CALCULAR_MS = 500

const DATOS_VACIOS: DatosDelFormulario = {
  nombre: '',
  telefono: '',
  correo: '',
  zona: '',
  consumoKwh: '',
  latitud: null,
  longitud: null,
}

type Estado = 'formulario' | 'enviando' | 'gracias'
type EstadoUbicacion = 'no' | 'pidiendo' | 'puesta' | 'fallo'

export function App() {
  const [opciones, setOpciones] = useState<OpcionesDelFormulario | null>(null)
  const [datos, setDatos] = useState<DatosDelFormulario>(DATOS_VACIOS)
  const [archivo, setArchivo] = useState<File | null>(null)
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [avisoGeneral, setAvisoGeneral] = useState('')
  const [estado, setEstado] = useState<Estado>('formulario')
  const [ubicacion, setUbicacion] = useState<EstadoUbicacion>('no')
  const [recomendacion, setRecomendacion] = useState<DatosRecomendacion | null>(null)
  const [calculando, setCalculando] = useState(false)

  useEffect(() => {
    cargarFormulario()
      .then(setOpciones)
      .catch(() =>
        setAvisoGeneral('No pudimos cargar el formulario. Recarga la página, por favor.'),
      )
  }, [])

  /**
   * Pide la recomendacion cuando hay consumo y zona, medio segundo despues de teclear.
   *
   * El `AbortController` no es decoracion: si alguien escribe "1000" y luego lo corrige
   * a "1500", la respuesta de la peticion vieja puede llegar DESPUES que la nueva y
   * dejar en pantalla el sistema del consumo equivocado. Se cancela la anterior y se
   * ignora lo que llegue de ella.
   */
  useEffect(() => {
    const consumo = Number(datos.consumoKwh)
    if (!datos.zona || !Number.isFinite(consumo) || consumo <= 0) {
      setRecomendacion(null)
      setCalculando(false)
      return
    }

    const control = new AbortController()
    setCalculando(true)
    const temporizador = setTimeout(async () => {
      const resultado = await pedirRecomendacion(consumo, datos.zona, control.signal)
      if (control.signal.aborted) return
      setRecomendacion(resultado)
      setCalculando(false)
    }, PAUSA_ANTES_DE_CALCULAR_MS)

    return () => {
      clearTimeout(temporizador)
      control.abort()
    }
  }, [datos.consumoKwh, datos.zona])

  /**
   * Si la persona escribio su consumo pero no ha elegido zona.
   *
   * Sin zona no hay irradiacion y el motor no puede recomendar nada, asi que la
   * tarjeta no aparecia y la pagina se quedaba muda: escribes tu consumo, no pasa
   * nada, y nada te dice por que. Le paso al propio Gern probando en produccion. Un
   * callejon sin salida silencioso es peor que un error, porque el error al menos se
   * puede leer.
   */
  const faltaLaZona = Number(datos.consumoKwh) > 0 && !datos.zona

  /** Lleva el foco al desplegable de zona, que esta mas arriba y puede quedar fuera de pantalla. */
  function irALaZona() {
    const select = document.getElementById('zona')
    select?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    ;(select as HTMLSelectElement | null)?.focus({ preventScroll: true })
  }

  function cambiar<C extends keyof DatosDelFormulario>(campo: C, valor: DatosDelFormulario[C]) {
    setDatos((antes) => ({ ...antes, [campo]: valor }))
    // El error se quita en cuanto la persona toca el campo. Dejarlo puesto mientras
    // corrige es discutir con quien ya te esta haciendo caso.
    setErrores((antes) => {
      if (!(campo in antes)) return antes
      const { [campo]: _quitado, ...resto } = antes
      return resto
    })
  }

  async function compartirUbicacion() {
    setUbicacion('pidiendo')
    try {
      const punto = await pedirUbicacion()
      setDatos((antes) => ({ ...antes, latitud: punto.latitud, longitud: punto.longitud }))
      setUbicacion('puesta')
    } catch {
      setUbicacion('fallo')
    }
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    setEstado('enviando')
    setAvisoGeneral('')
    setErrores({})

    const respuesta = await enviarSolicitud(datos, archivo)
    if (respuesta.ok) {
      setEstado('gracias')
      return
    }

    setEstado('formulario')
    setErrores(respuesta.errores ?? {})
    setAvisoGeneral(respuesta.error ?? '')
    // Si el error es de un campo concreto, se lleva el foco ahi: en un movil el mensaje
    // puede quedar fuera de pantalla y parece que el boton no hizo nada.
    const primero = Object.keys(respuesta.errores ?? {})[0]
    if (primero) document.getElementById(primero)?.focus()
  }

  function empezarDeNuevo() {
    setDatos(DATOS_VACIOS)
    setArchivo(null)
    setErrores({})
    setAvisoGeneral('')
    setUbicacion('no')
    setEstado('formulario')
  }

  return (
    <div className="pagina">
      <header className="cabecera">
        <a href="https://www.sunsmartec.com">
          <img
            src="https://www.sunsmartec.com/wp-content/uploads/2025/03/logo_sunsmartec.png"
            alt="SunSmart"
            height={44}
          />
        </a>
      </header>

      <main className="tarjeta">
        {estado === 'gracias' && opciones ? (
          <Gracias
            nombre={datos.nombre}
            contactoWhatsapp={opciones.contacto.whatsapp}
            contactoEmail={opciones.contacto.email}
            onOtra={empezarDeNuevo}
          />
        ) : (
          <>
            <h1>Cotiza tu sistema solar</h1>
            <p className="entradilla">
              Déjanos tus datos y tu consumo. Un asesor revisa tu caso y te contacta con una
              propuesta hecha para tu techo, no una tabla de precios genérica.
            </p>

            <form onSubmit={enviar} noValidate>
              <CampoTexto
                id="nombre"
                etiqueta="Tu nombre"
                valor={datos.nombre}
                onCambio={(v) => cambiar('nombre', v)}
                error={errores.nombre}
                requerido
                autoComplete="name"
              />

              {/* El teléfono va antes que el correo porque es por donde el asesor
                  contacta de verdad. El orden del formulario dice qué importa. */}
              <CampoTexto
                id="telefono"
                etiqueta="Tu teléfono"
                valor={datos.telefono}
                onCambio={(v) => cambiar('telefono', v)}
                tipo="text"
                inputMode="tel"
                error={errores.telefono}
                ayuda="Es por donde te vamos a llamar. Ej: 0991234567"
                requerido
                autoComplete="tel"
              />

              <CampoTexto
                id="correo"
                etiqueta="Tu correo"
                valor={datos.correo}
                onCambio={(v) => cambiar('correo', v)}
                tipo="email"
                inputMode="email"
                error={errores.correo}
                ayuda="Ahí te confirmamos que recibimos la solicitud."
                requerido
                autoComplete="email"
              />

              <div className="campo">
                <label htmlFor="zona">
                  ¿Dónde está la propiedad?<span aria-hidden="true"> *</span>
                </label>
                <select
                  id="zona"
                  value={datos.zona}
                  onChange={(evento) => cambiar('zona', evento.target.value)}
                  aria-invalid={errores.zona ? true : undefined}
                  className={errores.zona ? 'con-error' : undefined}
                >
                  <option value="">Elige tu zona…</option>
                  {opciones?.zonas.map((zona) => (
                    <option key={zona.valor} value={zona.valor}>
                      {zona.etiqueta}
                    </option>
                  ))}
                </select>
                {errores.zona && (
                  <p className="error" role="alert">
                    {errores.zona}
                  </p>
                )}
              </div>

              <BloqueConsumo
                archivo={archivo}
                onArchivo={(nuevo) => {
                  setArchivo(nuevo)
                  setErrores(({ planilla: _p, consumoKwh: _c, ...resto }) => resto)
                }}
                consumoKwh={datos.consumoKwh}
                onConsumo={(v) => cambiar('consumoKwh', v)}
                errorPlanilla={errores.planilla}
                errorConsumo={errores.consumoKwh}
              >
                {faltaLaZona ? (
                  <p className="aviso-falta-zona" aria-live="polite">
                    Para decirte qué sistema necesitas nos falta{' '}
                    <strong>dónde está la propiedad</strong>: el tamaño depende del sol
                    que le da a tu zona.{' '}
                    <button type="button" className="enlace-en-texto" onClick={irALaZona}>
                      Elegir mi zona
                    </button>
                  </p>
                ) : (
                  <Recomendacion datos={recomendacion} cargando={calculando} />
                )}
              </BloqueConsumo>

              <BotonUbicacion estado={ubicacion} onPedir={compartirUbicacion} />

              {avisoGeneral && (
                <p className="aviso-general" role="alert">
                  {avisoGeneral}
                </p>
              )}

              <button type="submit" className="boton-principal" disabled={estado === 'enviando'}>
                {estado === 'enviando' ? 'Enviando…' : 'Quiero mi propuesta'}
              </button>

              <p className="letra-pequena">
                Usamos tus datos solo para prepararte esta propuesta y contactarte. Tu planilla
                no se publica en ningún sitio.
              </p>
            </form>
          </>
        )}
      </main>

      <footer className="pie">
        <a href="https://www.sunsmartec.com">www.sunsmartec.com</a>
        {opciones && <span> · {opciones.contacto.email}</span>}
      </footer>
    </div>
  )
}

/**
 * El boton de ubicacion, con sus cuatro estados a la vista.
 *
 * Se explica QUE mejora antes de pedir el permiso. Un dialogo del navegador que aparece
 * sin motivo se rechaza casi siempre, y una vez rechazado no se puede volver a pedir.
 */
function BotonUbicacion({ estado, onPedir }: { estado: EstadoUbicacion; onPedir: () => void }) {
  if (estado === 'puesta') {
    return (
      <p className="ubicacion-ok" role="status">
        ✓ Ubicación recibida. Vamos a usar la radiación solar exacta de tu techo.
      </p>
    )
  }

  return (
    <div className="ubicacion">
      <button
        type="button"
        className="boton-secundario"
        onClick={onPedir}
        disabled={estado === 'pidiendo'}
      >
        {estado === 'pidiendo' ? 'Pidiendo permiso…' : 'Compartir mi ubicación (opcional)'}
      </button>
      <small>
        {estado === 'fallo'
          ? 'No pasa nada: usaremos la radiación solar de referencia de tu zona.'
          : 'Con tus coordenadas consultamos la radiación solar real de tu punto en vez del promedio de la ciudad.'}
      </small>
    </div>
  )
}
